import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Typography, Tag, Space, Empty, Select, Segmented, Spin, Table, Progress, Button } from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  BankOutlined,
  UserOutlined,
  PieChartOutlined,
  CalendarOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  ClusterOutlined,
  FileExcelOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { fetchDashboardAnalytics } from '../../../../Service/Dashboard_API/dashboardApi';

const { Text } = Typography;
const { Option } = Select;

// --- 1. Menu-Wise Activity Bar Chart (Independent Local Filters) ---
export const MenuWiseBarChart = ({ data = [] }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const processedData = useMemo(() => {
    if (!data) return [];
    let list = [...data];

    if (categoryFilter === 'curriculum') {
      list = list.filter((item) => ['courses', 'modules', 'video-index'].includes(item.key));
    } else if (categoryFilter === 'sessions') {
      list = list.filter((item) => ['live-sessions', 'session-mgmt', 'candidates-mgmt'].includes(item.key));
    }

    if (sortOrder === 'desc') {
      list.sort((a, b) => b.count - a.count);
    } else if (sortOrder === 'asc') {
      list.sort((a, b) => a.count - b.count);
    } else if (sortOrder === 'alpha') {
      list.sort((a, b) => a.menu.localeCompare(b.menu));
    }
    return list;
  }, [data, sortOrder, categoryFilter]);

  if (!data || data.length === 0) {
    return <Empty description="No menu analytics available" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const maxVal = Math.max(...processedData.map((d) => d.count), 1);

  return (
    <Card
      title={
        <Space>
          <BarChartOutlined style={{ color: '#3b82f6', fontSize: 18 }} />
          <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>
            Menu-Wise Activity Breakdown
          </Text>
        </Space>
      }
      extra={
        <Space size="small" wrap>
          <Select showSearch optionFilterProp="children"
            size="small"
            value={categoryFilter}
            onChange={(val) => setCategoryFilter(val)}
            style={{ width: 120 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">All Category</Option>
            <Option value="curriculum">Curriculum</Option>
            <Option value="sessions">Sessions & Ops</Option>
          </Select>

          <Select showSearch optionFilterProp="children"
            size="small"
            value={sortOrder}
            onChange={(val) => setSortOrder(val)}
            style={{ width: 100 }}
            popupMatchSelectWidth={false}
          >
            <Option value="desc">High → Low</Option>
            <Option value="asc">Low → High</Option>
            <Option value="alpha">A-Z Name</Option>
          </Select>
        </Space>
      }
      style={{
        borderRadius: 16,
        height: '100%',
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
        {processedData.map((item, idx) => {
          const pct = Math.min(100, Math.round((item.count / maxVal) * 100));
          const isHovered = hoveredIdx === idx;

          return (
            <div
              key={item.key || idx}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{
                transition: 'transform 0.2s ease',
                transform: isHovered ? 'translateX(4px)' : 'none'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text style={{ fontWeight: 600, color: 'var(--text-color, #374151)' }}>
                  {item.menu}
                </Text>
                <Tag color={item.color || 'blue'} style={{ fontWeight: 700, borderRadius: 12 }}>
                  {item.count.toLocaleString()} items
                </Tag>
              </div>

              <div
                style={{
                  height: 12,
                  width: '100%',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: 6,
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    backgroundColor: item.color || '#3b82f6',
                    background: `linear-gradient(90deg, ${item.color || '#3b82f6'} 0%, ${item.color || '#3b82f6'}dd 100%)`,
                    borderRadius: 6,
                    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isHovered ? `0 0 10px ${item.color}88` : 'none'
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// --- 2. Month-Wise Activity Chart (Self-Contained Card-Specific Academic Year Filter) ---
export const MonthWiseActivityChart = ({
  monthlyData = [],
  academicYears = [],
  selectedYearId = '4013'
}) => {
  const [cardYearId, setCardYearId] = useState(selectedYearId || '4013');
  const [currentMonthlyData, setCurrentMonthlyData] = useState(monthlyData);
  const [cardLoading, setCardLoading] = useState(false);
  const [metricFocus, setMetricFocus] = useState('all');
  const [viewStyle, setViewStyle] = useState('area');

  useEffect(() => {
    if (monthlyData && monthlyData.length > 0) {
      setCurrentMonthlyData(monthlyData);
    }
  }, [monthlyData]);

  const fallbackYears = useMemo(() => [
    { academic_year_id: 4013, academic_year: '2026-2027' },
    { academic_year_id: 4012, academic_year: '2025-2026' },
    { academic_year_id: 4011, academic_year: '2024-2025' },
    { academic_year_id: 4010, academic_year: '2023-2024' },
    { academic_year_id: 4009, academic_year: '2022-2023' }
  ], []);

  const yearList = (academicYears && academicYears.length > 0) ? academicYears : fallbackYears;
  const activeValue = String(cardYearId || yearList[0]?.academic_year_id || '4013');

  const handleCardYearChange = async (val) => {
    setCardYearId(val);
    try {
      setCardLoading(true);
      const res = await fetchDashboardAnalytics({ academic_year_id: val });
      if (res && res.success && res.monthlyTrend) {
        setCurrentMonthlyData(res.monthlyTrend);
      }
    } catch (err) {
      console.error('Failed to load card-specific monthly data:', err);
    } finally {
      setCardLoading(false);
    }
  };

  const chartData = currentMonthlyData || [];

  if (!chartData || chartData.length === 0) {
    return <Empty description="No monthly activity data" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const svgWidth = 550;
  const svgHeight = 220;
  const padding = 35;

  const dailySessionsVals = chartData.map((d) => d.daily_sessions || 0);
  const videosVals = chartData.map((d) => d.videos_indexed || 0);
  const liveVals = chartData.map((d) => d.live_streams || 0);

  const maxVal = Math.max(...dailySessionsVals, ...videosVals, ...liveVals, 10);

  const getX = (index) => {
    if (chartData.length <= 1) return svgWidth / 2;
    return padding + (index / (chartData.length - 1)) * (svgWidth - 2 * padding);
  };

  const getY = (val) => {
    return svgHeight - padding - (val / maxVal) * (svgHeight - 2 * padding);
  };

  const makePath = (vals) => {
    if (vals.length === 0) return '';
    return vals.reduce((acc, val, i) => {
      const x = getX(i);
      const y = getY(val);
      return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
    }, '');
  };

  const makeAreaPath = (vals) => {
    if (vals.length === 0) return '';
    const lineP = makePath(vals);
    const lastX = getX(vals.length - 1);
    const firstX = getX(0);
    const bottomY = svgHeight - padding;
    return `${lineP} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
  };

  return (
    <Card
      title={
        <Space direction="vertical" size={2}>
          <Space>
            <LineChartOutlined style={{ color: '#10b981', fontSize: 18 }} />
            <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>
              Month-Wise Activity Breakdown
            </Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            12-month activity progression for selected academic year
          </Text>
        </Space>
      }
      extra={
        <Space size="small" wrap align="center">
          <Select showSearch optionFilterProp="children"
            size="small"
            value={activeValue}
            onChange={handleCardYearChange}
            style={{ width: 130 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">All Years</Option>
            {yearList.map((ay) => (
              <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                <CalendarOutlined style={{ marginRight: 6, color: '#2563eb' }} />
                {ay.academic_year}
              </Option>
            ))}
          </Select>

          <Select showSearch optionFilterProp="children"
            size="small"
            value={metricFocus}
            onChange={(val) => setMetricFocus(val)}
            style={{ width: 110 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">All Metrics</Option>
            <Option value="sessions">Sessions</Option>
            <Option value="videos">Videos</Option>
            <Option value="live">Live Streams</Option>
          </Select>

          <Segmented
            size="small"
            options={[
              { label: 'Area', value: 'area' },
              { label: 'Line', value: 'line' }
            ]}
            value={viewStyle}
            onChange={(val) => setViewStyle(val)}
          />
        </Space>
      }
      style={{
        borderRadius: 16,
        height: '100%',
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <Spin spinning={cardLoading}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Space size="middle">
            {(metricFocus === 'all' || metricFocus === 'sessions') && (
              <Space size={4}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
                <Text style={{ fontSize: 11 }}>Daily Sessions</Text>
              </Space>
            )}
            {(metricFocus === 'all' || metricFocus === 'videos') && (
              <Space size={4}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#8b5cf6', display: 'inline-block' }} />
                <Text style={{ fontSize: 11 }}>Indexed Videos</Text>
              </Space>
            )}
            {(metricFocus === 'all' || metricFocus === 'live') && (
              <Space size={4}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f43f5e', display: 'inline-block' }} />
                <Text style={{ fontSize: 11 }}>Live Streams</Text>
              </Space>
            )}
          </Space>
        </div>

        <div style={{ width: '100%', overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: 'auto' }}>
            <defs>
              <linearGradient id="areaGradientBlue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
              </linearGradient>
              <linearGradient id="areaGradientPurple" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
              const y = padding + pct * (svgHeight - 2 * padding);
              return (
                <line
                  key={i}
                  x1={padding}
                  y1={y}
                  x2={svgWidth - padding}
                  y2={y}
                  stroke="rgba(150, 150, 150, 0.15)"
                  strokeDasharray="4 4"
                />
              );
            })}

            {viewStyle === 'area' && (metricFocus === 'all' || metricFocus === 'sessions') && (
              <path d={makeAreaPath(dailySessionsVals)} fill="url(#areaGradientBlue)" />
            )}
            {viewStyle === 'area' && (metricFocus === 'all' || metricFocus === 'videos') && (
              <path d={makeAreaPath(videosVals)} fill="url(#areaGradientPurple)" />
            )}

            {(metricFocus === 'all' || metricFocus === 'sessions') && (
              <path d={makePath(dailySessionsVals)} fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
            )}
            {(metricFocus === 'all' || metricFocus === 'videos') && (
              <path d={makePath(videosVals)} fill="none" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" />
            )}
            {(metricFocus === 'all' || metricFocus === 'live') && (
              <path d={makePath(liveVals)} fill="none" stroke="#f43f5e" strokeWidth="3" strokeLinecap="round" />
            )}

            {chartData.map((d, i) => {
              const x = getX(i);
              const yBlue = getY(d.daily_sessions || 0);
              const yPurple = getY(d.videos_indexed || 0);
              const yRed = getY(d.live_streams || 0);

              const displayLabel = d.academic_year || '';

              return (
                <g key={i}>
                  {(metricFocus === 'all' || metricFocus === 'sessions') && (
                    <circle cx={x} cy={yBlue} r={4} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
                  )}
                  {(metricFocus === 'all' || metricFocus === 'videos') && (
                    <circle cx={x} cy={yPurple} r={4} fill="#8b5cf6" stroke="#fff" strokeWidth={2} />
                  )}
                  {(metricFocus === 'all' || metricFocus === 'live') && (
                    <circle cx={x} cy={yRed} r={4} fill="#f43f5e" stroke="#fff" strokeWidth={2} />
                  )}
                  <text
                    x={x}
                    y={svgHeight - 10}
                    textAnchor="middle"
                    fill="var(--text-secondary, #6b7280)"
                    fontSize={9}
                    fontWeight={500}
                  >
                    {displayLabel}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </Spin>
    </Card>
  );
};

// --- 3. Institution-Wise Performance Bar Chart (Independent Local Filters) ---
export const InstitutionBarChart = ({ data = [] }) => {
  const [metricFilter, setMetricFilter] = useState('all');
  const [topLimit, setTopLimit] = useState(6);

  const processedData = useMemo(() => {
    if (!data) return [];
    return data.slice(0, topLimit);
  }, [data, topLimit]);

  if (!data || data.length === 0) {
    return <Empty description="No institution statistics" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const maxVal = Math.max(...processedData.map((d) => d.live_sessions_count || 0), ...processedData.map((d) => d.total_candidates || 0), 1);

  return (
    <Card
      title={
        <Space>
          <BankOutlined style={{ color: '#8b5cf6', fontSize: 18 }} />
          <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>
            Institution Performance & Engagement
          </Text>
        </Space>
      }
      extra={
        <Space size="small" wrap>
          <Select showSearch optionFilterProp="children"
            size="small"
            value={metricFilter}
            onChange={(val) => setMetricFilter(val)}
            style={{ width: 110 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">Both Metrics</Option>
            <Option value="live">Live Only</Option>
            <Option value="candidates">Candidates Only</Option>
          </Select>

          <Select showSearch optionFilterProp="children"
            size="small"
            value={topLimit}
            onChange={(val) => setTopLimit(val)}
            style={{ width: 85 }}
            popupMatchSelectWidth={false}
          >
            <Option value={5}>Top 5</Option>
            <Option value={6}>Top 6</Option>
            <Option value={10}>Top 10</Option>
          </Select>
        </Space>
      }
      style={{
        borderRadius: 16,
        height: '100%',
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {processedData.map((inst, idx) => {
          const sessionsPct = Math.min(100, Math.round(((inst.live_sessions_count || 0) / maxVal) * 100));
          const candidatesPct = Math.min(100, Math.round(((inst.total_candidates || 0) / maxVal) * 100));

          return (
            <div key={inst.institution_id || idx}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text ellipsis style={{ maxWidth: '65%', fontWeight: 600, color: 'var(--text-color, #374151)' }}>
                  {inst.institution_name}
                </Text>
                <Space size={6}>
                  {(metricFilter === 'all' || metricFilter === 'live') && (
                    <Tag color="magenta">{inst.live_sessions_count || 0} Live</Tag>
                  )}
                  {(metricFilter === 'all' || metricFilter === 'candidates') && (
                    <Tag color="cyan">{inst.total_candidates || 0} Candidates</Tag>
                  )}
                </Space>
              </div>

              <div style={{ display: 'flex', gap: 4, height: 8 }}>
                {(metricFilter === 'all' || metricFilter === 'live') && (
                  <div
                    style={{
                      height: '100%',
                      width: `${sessionsPct}%`,
                      backgroundColor: '#e11d48',
                      borderRadius: 4,
                      transition: 'width 0.6s ease'
                    }}
                  />
                )}
                {(metricFilter === 'all' || metricFilter === 'candidates') && (
                  <div
                    style={{
                      height: '100%',
                      width: `${candidatesPct}%`,
                      backgroundColor: '#06b6d4',
                      borderRadius: 4,
                      transition: 'width 0.6s ease'
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// --- 4. Instructor Performance Chart (Independent Local Filters) ---
export const InstructorPerformanceChart = ({ data = [] }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [mediumFilter, setMediumFilter] = useState('all');

  const processedData = useMemo(() => {
    if (!data) return [];
    let list = [...data];
    if (statusFilter !== 'all') {
      list = list.filter((ins) => ins.status === statusFilter);
    }
    if (mediumFilter !== 'all') {
      list = list.filter((ins) => ins.instructor_medium && ins.instructor_medium.includes(mediumFilter));
    }
    return list;
  }, [data, statusFilter, mediumFilter]);

  if (!data || data.length === 0) {
    return <Empty description="No instructor statistics" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const maxVal = Math.max(...processedData.map((d) => d.sessions_delivered || 0), 1);

  return (
    <Card
      title={
        <Space>
          <UserOutlined style={{ color: '#f59e0b', fontSize: 18 }} />
          <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>
            Instructor Class Delivery Leaderboard
          </Text>
        </Space>
      }
      extra={
        <Space size="small" wrap>
          <Select showSearch optionFilterProp="children"
            size="small"
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            style={{ width: 100 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">All Status</Option>
            <Option value="Active">Active</Option>
            <Option value="Inactive">Inactive</Option>
          </Select>

          <Select showSearch optionFilterProp="children"
            size="small"
            value={mediumFilter}
            onChange={(val) => setMediumFilter(val)}
            style={{ width: 110 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">All Mediums</Option>
            <Option value="KANNADA">Kannada</Option>
            <Option value="ENGLISH">English</Option>
          </Select>
        </Space>
      }
      style={{
        borderRadius: 16,
        height: '100%',
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {processedData.slice(0, 6).map((ins, idx) => {
          const pct = Math.min(100, Math.round(((ins.sessions_delivered || 0) / maxVal) * 100));

          return (
            <div key={ins.instructor_id || idx} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: idx < 3 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#e5e7eb',
                  color: idx < 3 ? '#ffffff' : '#4b5563',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 12
                }}
              >
                {idx + 1}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontWeight: 600, color: 'var(--text-color, #374151)' }}>
                    {ins.instructor_name}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 700 }}>
                    {ins.sessions_delivered || 0} Classes
                  </Text>
                </div>

                <div
                  style={{
                    height: 8,
                    width: '100%',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${pct}%`,
                      backgroundColor: '#f59e0b',
                      borderRadius: 4,
                      transition: 'width 0.6s ease'
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// --- 5. Subject Distribution Donut Chart (Independent Local Filters) ---
export const SubjectDonutChart = ({ data = [] }) => {
  const [topLimit, setTopLimit] = useState(8);

  const processedData = useMemo(() => {
    if (!data) return [];
    return data.slice(0, topLimit);
  }, [data, topLimit]);

  if (!data || data.length === 0) {
    return <Empty description="No subject distribution data" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f43f5e', '#f59e0b', '#06b6d4', '#ec4899', '#6366f1'];
  const totalSessions = processedData.reduce((acc, curr) => acc + (curr.session_count || 0), 0);

  let cumulativeAngle = 0;

  return (
    <Card
      title={
        <Space>
          <PieChartOutlined style={{ color: '#ec4899', fontSize: 18 }} />
          <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>
            Subject Content Share
          </Text>
        </Space>
      }
      extra={
        <Select showSearch optionFilterProp="children"
          size="small"
          value={topLimit}
          onChange={(val) => setTopLimit(val)}
          style={{ width: 95 }}
          popupMatchSelectWidth={false}
        >
          <Option value={5}>Top 5</Option>
          <Option value={8}>Top 8</Option>
          <Option value={15}>All Subjects</Option>
        </Select>
      }
      style={{
        borderRadius: 16,
        height: '100%',
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: 140, height: 140 }}>
            <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              {processedData.map((item, idx) => {
                const count = item.session_count || 0;
                const pct = totalSessions > 0 ? count / totalSessions : 0;
                const strokeDasharray = `${pct * 283} 283`;
                const strokeDashoffset = -cumulativeAngle * 283;
                cumulativeAngle += pct;

                return (
                  <circle
                    key={item.subjects_id || idx}
                    cx="50"
                    cy="50"
                    r="45"
                    fill="transparent"
                    stroke={colors[idx % colors.length]}
                    strokeWidth="10"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    style={{ transition: 'stroke-dasharray 0.8s ease' }}
                  />
                );
              })}
            </svg>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}
            >
              <Text style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{totalSessions}</Text>
              <Text type="secondary" style={{ fontSize: 10 }}>Total Sessions</Text>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {processedData.map((item, idx) => {
              const count = item.session_count || 0;
              const pct = totalSessions > 0 ? Math.round((count / totalSessions) * 100) : 0;

              return (
                <div key={item.subjects_id || idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Space size={6}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: colors[idx % colors.length],
                        display: 'inline-block'
                      }}
                    />
                    <Text style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-color, #374151)' }}>
                      {item.subject_name}
                    </Text>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
                    {pct}% ({count})
                  </Text>
                </div>
              );
            })}
          </div>
        </Col>
      </Row>
    </Card>
  );
};

// --- 6. Candidates Performance Analytics Card ---
export const ClusterInstitutionAnalyticsCard = ({
  InstitutionsData = [],
  academicYears = [],
  clusters = []
}) => {
  const role = localStorage.getItem('role');
  const isStudio = role === 'STUDIO';
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedCluster, setSelectedCluster] = useState('all'); // stores cluster_id
  const [viewMode, setViewMode] = useState('both'); // 'both', 'charts', 'table'
  const [currentInstitutionsData, setCurrentInstitutionsData] = useState(InstitutionsData || []);
  const [cardLoading, setCardLoading] = useState(false);

  // Sync internal data when InstitutionsData prop updates (only if it contains data matching the selected year)
  useEffect(() => {
    if (Array.isArray(InstitutionsData) && InstitutionsData.length > 0) {
      const hasMatchingData = selectedYear === 'all' || InstitutionsData.some(s => String(s.academic_year_id) === String(selectedYear));
      if (hasMatchingData) {
        setCurrentInstitutionsData(InstitutionsData);
      }
    }
  }, [InstitutionsData, selectedYear]);

  // Filter academicYears so only completed academic years whose end date has passed are shown (exclude current ongoing academic year)
  const completedAcademicYears = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return (academicYears || [])
      .filter(ay => {
        if (!ay.end_date) return false;
        const endDate = new Date(ay.end_date);
        return !isNaN(endDate.getTime()) && endDate <= today;
      })
      .sort((a, b) => (b.academic_year_id || 0) - (a.academic_year_id || 0));
  }, [academicYears]);

  // Set default selectedYear to the latest completed (last) academic year
  useEffect(() => {
    if (completedAcademicYears && completedAcademicYears.length > 0) {
      const latestCompletedAyId = String(completedAcademicYears[0].academic_year_id);
      setSelectedYear((prev) => {
        const isValid = prev && prev !== 'all' && completedAcademicYears.some(ay => String(ay.academic_year_id) === String(prev));
        return isValid ? prev : latestCompletedAyId;
      });
    }
  }, [completedAcademicYears]);

  const selectedYearName = useMemo(() => {
    if (!selectedYear || selectedYear === 'all') return 'All Completed Academic Years';
    const found = (completedAcademicYears || []).find(ay => String(ay.academic_year_id) === String(selectedYear));
    return found ? found.academic_year : 'Unknown';
  }, [completedAcademicYears, selectedYear]);

  const [lastUpdatedTime, setLastUpdatedTime] = useState(new Date());
  const [isLiveUpdating, setIsLiveUpdating] = useState(false);

  // Fetch card-level data with background silent mode support
  const loadCardData = useCallback(async (yearId, isBackground = false) => {
    if (!yearId) return;
    try {
      if (!isBackground) {
        setCardLoading(true);
      } else {
        setIsLiveUpdating(true);
      }
      const queryParams = {};
      if (yearId && yearId !== 'all') {
        queryParams.academic_year_id = yearId;
      }
      const res = await fetchDashboardAnalytics(queryParams);
      if (res && res.success && Array.isArray(res.clusterInstitutionStats)) {
        setCurrentInstitutionsData(res.clusterInstitutionStats);
        setLastUpdatedTime(new Date());
      }
    } catch (err) {
      console.error('Failed to load card-specific candidate performance data:', err);
    } finally {
      if (!isBackground) {
        setCardLoading(false);
      }
      setIsLiveUpdating(false);
    }
  }, []);

  const handleCardYearChange = (val) => {
    setSelectedYear(val);
    loadCardData(val, false);
  };

  // Real-time automatic polling every 10 seconds & event listener for live updates
  useEffect(() => {
    if (selectedYear) {
      // 1. Initial load
      loadCardData(selectedYear, false);

      // 2. Periodic background poll every 10 seconds
      const pollInterval = setInterval(() => {
        loadCardData(selectedYear, true);
      }, 10000);

      // 3. Custom event listener for instant updates when candidates change
      const handleCandidateUpdateEvent = () => {
        loadCardData(selectedYear, true);
      };
      window.addEventListener('candidatesUpdated', handleCandidateUpdateEvent);
      window.addEventListener('focus', handleCandidateUpdateEvent);

      return () => {
        clearInterval(pollInterval);
        window.removeEventListener('candidatesUpdated', handleCandidateUpdateEvent);
        window.removeEventListener('focus', handleCandidateUpdateEvent);
      };
    }
  }, [selectedYear, loadCardData]);

  const rawList = useMemo(() => currentInstitutionsData || [], [currentInstitutionsData]);

  // Derive available clusters strictly for the selected academic year
  const availableClusters = useMemo(() => {
    const map = new Map();

    // 1. Add clusters found in candidate data for the selected academic year
    let filteredData = rawList;
    if (selectedYear && selectedYear !== 'all') {
      filteredData = rawList.filter(s => String(s.academic_year_id) === String(selectedYear));
    }
    filteredData.forEach(s => {
      if (s.cluster_id != null) {
        const cIdStr = String(s.cluster_id);
        if (!map.has(cIdStr)) {
          map.set(cIdStr, {
            cluster_id: s.cluster_id,
            cluster_name: s.cluster_name || `Cluster #${s.cluster_id}`
          });
        }
      }
    });

    // 2. Add clusters from metadata prop strictly matching the selected academic year
    if (clusters && clusters.length > 0) {
      clusters.forEach(cl => {
        const cId = cl.cluster_id || cl;
        const cName = typeof cl === 'string' ? cl : (cl.cluster_name || `Cluster #${cId}`);
        const cAyId = cl.academic_year_id != null ? String(cl.academic_year_id) : null;

        // If an academic year is selected, only include clusters matching that academic year
        const matchesYear = selectedYear === 'all' || (cAyId && cAyId === String(selectedYear));
        if (cId && matchesYear) {
          const cIdStr = String(cId);
          if (!map.has(cIdStr)) {
            map.set(cIdStr, {
              cluster_id: cId,
              cluster_name: cName
            });
          }
        }
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.cluster_name || '').localeCompare(b.cluster_name || '', undefined, { numeric: true })
    );
  }, [rawList, selectedYear, clusters]);

  const selectedClusterObj = useMemo(() => {
    if (!selectedCluster || selectedCluster === 'all') return null;
    return availableClusters.find(c => String(c.cluster_id) === String(selectedCluster)) || null;
  }, [availableClusters, selectedCluster]);

  const selectedClusterName = selectedClusterObj ? selectedClusterObj.cluster_name : null;

  // Reset or default selectedCluster based on role and availableClusters in the selected year
  useEffect(() => {
    if (isStudio) {
      if (availableClusters.length > 0) {
        if (selectedCluster === 'all' || !availableClusters.some(cl => String(cl.cluster_id) === String(selectedCluster))) {
          setSelectedCluster(String(availableClusters[0].cluster_id));
        }
      }
    } else {
      if (selectedCluster !== 'all') {
        const exists = availableClusters.some(cl => String(cl.cluster_id) === String(selectedCluster));
        if (!exists) {
          setSelectedCluster('all');
        }
      }
    }
  }, [availableClusters, selectedCluster, isStudio]);

  // Local filtering by cluster & academic year with Cluster-Wise Sorting
  const processedInstitutions = useMemo(() => {
    let list = [...rawList];
    if (selectedYear && selectedYear !== 'all') {
      list = list.filter(s => String(s.academic_year_id) === String(selectedYear));
    }
    if (selectedCluster && selectedCluster !== 'all') {
      list = list.filter(s =>
        String(s.cluster_id) === String(selectedCluster) ||
        (selectedClusterName && s.cluster_name && s.cluster_name.trim().toLowerCase() === selectedClusterName.trim().toLowerCase())
      );
    }
    list.sort((a, b) => {
      const cComp = (a.cluster_name || '').localeCompare(b.cluster_name || '');
      if (cComp !== 0) return cComp;
      const sComp = (a.standard_name || '').localeCompare(b.standard_name || '');
      if (sComp !== 0) return sComp;
      return (a.code || '').localeCompare(b.code || '');
    });
    return list;
  }, [rawList, selectedCluster, selectedClusterName, selectedYear]);

  const groupedInstitutions = useMemo(() => {
    // Group by cluster_id + standard_name to prevent institutions from different clusters
    // with the same standard name from bleeding into each other
    const groups = {};
    processedInstitutions.forEach(s => {
      const clusterKey = String(s.cluster_id ?? 'no-cluster');
      const st = s.standard_name || 'Unassigned Standard';
      const key = `${clusterKey}||${st}`;
      if (!groups[key]) groups[key] = { cluster_name: s.cluster_name || '', standard: st, institutions: [] };
      groups[key].institutions.push(s);
    });
    return Object.keys(groups)
      .sort((a, b) => {
        const ga = groups[a], gb = groups[b];
        const cComp = (ga.cluster_name || '').localeCompare(gb.cluster_name || '');
        return cComp !== 0 ? cComp : (ga.standard || '').localeCompare(gb.standard || '');
      })
      .map(key => ({
        standard: groups[key].standard,
        cluster_name: groups[key].cluster_name,
        institutions: groups[key].institutions
      }));
  }, [processedInstitutions]);

  // Aggregates for summary bar & Pie chart
  const summaryMetrics = useMemo(() => {
    const totalStudents = processedInstitutions.reduce((acc, s) => acc + (s.total_students || 0), 0);
    const totalPass = processedInstitutions.reduce((acc, s) => acc + (s.total_pass || 0), 0);
    const totalFail = processedInstitutions.reduce((acc, s) => acc + (s.fail || 0), 0);
    const totalDistinction = processedInstitutions.reduce((acc, s) => acc + (s.distinction || 0), 0);
    const totalFirstClass = processedInstitutions.reduce((acc, s) => acc + (s.first_class || 0), 0);
    const totalSecondClass = processedInstitutions.reduce((acc, s) => acc + (s.second_class || 0), 0);
    const totalPassOnly = processedInstitutions.reduce((acc, s) => acc + (s.pass || 0), 0);
    const totalAbove600 = processedInstitutions.reduce((acc, s) => acc + (s.above_600 || 0), 0);

    const highestMarks = Math.max(...processedInstitutions.map(s => s.highest_marks || 0), 0);
    const passPct = totalStudents > 0 ? (totalPass / totalStudents * 100).toFixed(2) : '0.00';

    return {
      totalStudents,
      totalPass,
      totalFail,
      totalDistinction,
      totalFirstClass,
      totalSecondClass,
      totalPassOnly,
      totalAbove600,
      highestMarks,
      passPct
    };
  }, [processedInstitutions]);



  // --- Export Helpers ---
  const handleExportExcel = () => {
    if (!processedInstitutions || processedInstitutions.length === 0) return;

    const headers = [
      'Standard',
      'Code',
      'Institution Name',
      'Total Students',
      'Highest Marks',
      'Above 600',
      'Distinction',
      'First Class',
      'Second Class',
      'Pass',
      'Total Pass',
      'Fail',
      'Pass %'
    ];

    const rows = [];
    groupedInstitutions.forEach(({ standard, institutions }) => {
      institutions.forEach(s => {
        rows.push([
          `"${institutions[0]?.cluster_name ? `${institutions[0].cluster_name} - ` : ''}${standard}${institutions[0]?.academic_year_name ? ` - ${institutions[0].academic_year_name}` : ''}"`,
          `"${s.code || ''}"`,
          `"${(s.Institution_name || '').replace(/"/g, '""')}"`,
          s.total_students || 0,
          s.highest_marks || 0,
          s.above_600 || 0,
          s.distinction || 0,
          s.first_class || 0,
          s.second_class || 0,
          s.pass || 0,
          s.total_pass || 0,
          s.fail || 0,
          `"${s.pass_pct || 0}%"`
        ]);
      });

      const stdTotalStudents = institutions.reduce((acc, s) => acc + (s.total_students || 0), 0);
      const stdTotalPass = institutions.reduce((acc, s) => acc + (s.total_pass || 0), 0);
      const stdTotalFail = institutions.reduce((acc, s) => acc + (s.fail || 0), 0);
      const stdHighestMarks = Math.max(...institutions.map(s => s.highest_marks || 0), 0);
      const stdTotalDistinction = institutions.reduce((acc, s) => acc + (s.distinction || 0), 0);
      const stdTotalFirstClass = institutions.reduce((acc, s) => acc + (s.first_class || 0), 0);
      const stdTotalSecondClass = institutions.reduce((acc, s) => acc + (s.second_class || 0), 0);
      const stdTotalPassOnly = institutions.reduce((acc, s) => acc + (s.pass || 0), 0);
      const stdTotalAbove600 = institutions.reduce((acc, s) => acc + (s.above_600 || 0), 0);
      const stdPassPct = stdTotalStudents > 0 ? (stdTotalPass / stdTotalStudents * 100).toFixed(2) : '0.00';

      rows.push([
        `"${institutions[0]?.cluster_name ? `${institutions[0].cluster_name} - ` : ''}${standard}${institutions[0]?.academic_year_name ? ` - ${institutions[0].academic_year_name}` : ''} SUMMARY"`,
        '""',
        '""',
        stdTotalStudents,
        stdHighestMarks,
        stdTotalAbove600,
        stdTotalDistinction,
        stdTotalFirstClass,
        stdTotalSecondClass,
        stdTotalPassOnly,
        stdTotalPass,
        stdTotalFail,
        `"${stdPassPct}%"`
      ]);
      rows.push([]); // empty row for separation
    });

    rows.push([
      '"OVERALL SUMMARY"',
      '""',
      '""',
      summaryMetrics.totalStudents,
      summaryMetrics.highestMarks,
      summaryMetrics.totalAbove600,
      summaryMetrics.totalDistinction,
      summaryMetrics.totalFirstClass,
      summaryMetrics.totalSecondClass,
      summaryMetrics.totalPassOnly,
      summaryMetrics.totalPass,
      summaryMetrics.totalFail,
      `"${summaryMetrics.passPct}%"`
    ]);

    const metadataRows = [
      ['"Candidates Performance Analytics"'],
      [`"Academic Year: ${selectedYearName}"`],
      [`"Cluster: ${selectedClusterName || 'All Clusters'}"`],
      [`"Generated: ${new Date().toLocaleString()}"`],
      []
    ];
    const csvContent = '\uFEFF' + [
      ...metadataRows.map(e => e.join(',')),
      headers.join(','),
      ...rows.map(e => e.join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const clusterFilePrefix = (selectedClusterName || 'All_Clusters').replace(/[^a-zA-Z0-9]/g, '_');
    const yearFilePrefix = (selectedYearName || 'All_Years').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `Candidates_Performance_Analytics_${clusterFilePrefix}_${yearFilePrefix}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (!processedInstitutions || processedInstitutions.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Candidates Performance Analytics - ${selectedClusterName || 'All Clusters'} (${selectedYearName})</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1f2937; }
            h2 { color: #1e3a8a; border-bottom: 2px solid #3b82f6; padding-bottom: 8px; margin-bottom: 16px; }
            .summary-box { display: flex; gap: 16px; background: #f0f9ff; padding: 12px 16px; border-radius: 8px; margin-bottom: 20px; border: 1px solid #bae6fd; }
            .summary-item { flex: 1; text-align: center; }
            .summary-item .label { font-size: 11px; color: #64748b; text-transform: uppercase; }
            .summary-item .val { font-size: 18px; font-weight: bold; color: #0f172a; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 10px; text-align: center; }
            th { background-color: #3b82f6; color: white; font-weight: 600; }
            td.left { text-align: left; }
            tr:nth-child(even) { background-color: #f8fafc; }
            tr.summary-row { background-color: #e0f2fe; font-weight: bold; }
            @media print {
              body { padding: 0; }
              @page { size: A4 landscape; margin: 12mm; }
            }
          </style>
        </head>
        <body>
          <h2>Candidates Performance Analytics</h2>
          <div style="margin-bottom: 16px; font-size: 13px; color: #4b5563; display: flex; justify-content: space-between;">
            <span><strong>Academic Year:</strong> ${selectedYearName} | <strong>Cluster:</strong> ${selectedClusterName || 'All Clusters'}</span>
            <span><strong>Generated On:</strong> ${new Date().toLocaleString()}</span>
          </div>
          <div class="summary-box">
            <div class="summary-item"><div class="label">Total Students</div><div class="val">${summaryMetrics.totalStudents}</div></div>
            <div class="summary-item"><div class="label">Total Pass</div><div class="val">${summaryMetrics.totalPass}</div></div>
            <div class="summary-item"><div class="label">Pass %</div><div class="val">${summaryMetrics.passPct}%</div></div>
            <div class="summary-item"><div class="label">Highest Marks</div><div class="val">${summaryMetrics.highestMarks}</div></div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Institution Name</th>
                <th>Total Students</th>
                <th>Highest Marks</th>
                <th>Above 600</th>
                <th>Distinction</th>
                <th>First Class</th>
                <th>Second Class</th>
                <th>Pass</th>
                <th>Total Pass</th>
                <th>Fail</th>
                <th>Pass %</th>
              </tr>
            </thead>
            <tbody>
          ${groupedInstitutions.map(({ standard, institutions }) => {
            const stdTotalStudents = institutions.reduce((acc, s) => acc + (s.total_students || 0), 0);
            const stdTotalPass = institutions.reduce((acc, s) => acc + (s.total_pass || 0), 0);
            const stdTotalFail = institutions.reduce((acc, s) => acc + (s.fail || 0), 0);
            const stdHighestMarks = Math.max(...institutions.map(s => s.highest_marks || 0), 0);
            const stdTotalDistinction = institutions.reduce((acc, s) => acc + (s.distinction || 0), 0);
            const stdTotalFirstClass = institutions.reduce((acc, s) => acc + (s.first_class || 0), 0);
            const stdTotalSecondClass = institutions.reduce((acc, s) => acc + (s.second_class || 0), 0);
            const stdTotalPassOnly = institutions.reduce((acc, s) => acc + (s.pass || 0), 0);
            const stdTotalAbove600 = institutions.reduce((acc, s) => acc + (s.above_600 || 0), 0);
            const stdPassPct = stdTotalStudents > 0 ? (stdTotalPass / stdTotalStudents * 100).toFixed(2) : '0.00';

            return `
              <tr><td colspan="12" style="background-color: #f1f5f9; text-align: left; font-weight: bold; color: #1e40af;">${institutions[0]?.cluster_name ? `${institutions[0].cluster_name} - ` : ''}${standard}${institutions[0]?.academic_year_name ? ` - ${institutions[0].academic_year_name}` : ''}</td></tr>
              ${institutions.map(s => `
                <tr>
                  <td>${s.code || ''}</td>
                  <td class="left">${s.Institution_name || ''}</td>
                  <td>${s.total_students || 0}</td>
                  <td>${s.highest_marks || '-'}</td>
                  <td>${s.above_600 || 0}</td>
                  <td>${s.distinction || 0}</td>
                  <td>${s.first_class || 0}</td>
                  <td>${s.second_class || 0}</td>
                  <td>${s.pass || 0}</td>
                  <td>${s.total_pass || 0}</td>
                  <td>${s.fail || 0}</td>
                  <td><b>${s.pass_pct || 0}%</b></td>
                </tr>
              `).join('')}
              <tr class="summary-row">
                <td colspan="2" class="left">${institutions[0]?.cluster_name ? `${institutions[0].cluster_name} - ` : ''}${standard}${institutions[0]?.academic_year_name ? ` - ${institutions[0].academic_year_name}` : ''} SUMMARY</td>
                <td>${stdTotalStudents}</td>
                <td>${stdHighestMarks}</td>
                <td>${stdTotalAbove600}</td>
                <td>${stdTotalDistinction}</td>
                <td>${stdTotalFirstClass}</td>
                <td>${stdTotalSecondClass}</td>
                <td>${stdTotalPassOnly}</td>
                <td>${stdTotalPass}</td>
                <td>${stdTotalFail}</td>
                <td>${stdPassPct}%</td>
              </tr>
            `;
          }).join('')}
              <tr class="summary-row" style="background-color: #bfdbfe;">
                <td colspan="2" class="left">OVERALL CLUSTER SUMMARY</td>
                <td>${summaryMetrics.totalStudents}</td>
                <td>${summaryMetrics.highestMarks}</td>
                <td>${summaryMetrics.totalAbove600}</td>
                <td>${summaryMetrics.totalDistinction}</td>
                <td>${summaryMetrics.totalFirstClass}</td>
                <td>${summaryMetrics.totalSecondClass}</td>
                <td>${summaryMetrics.totalPassOnly}</td>
                <td>${summaryMetrics.totalPass}</td>
                <td>${summaryMetrics.totalFail}</td>
                <td>${summaryMetrics.passPct}%</td>
              </tr>
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 90, render: (text) => <Tag color="geekblue" style={{ fontWeight: 700 }}>{text}</Tag> },
    { title: 'Institution Name', dataIndex: 'Institution_name', key: 'Institution_name', render: (name) => <Text strong style={{ color: 'var(--text-color, #1f2937)' }}>{name}</Text> },
    { title: 'Total Students', dataIndex: 'total_students', key: 'total_students', align: 'center', render: (val) => <Text style={{ fontWeight: 700 }}>{val}</Text> },
    { title: 'Highest Marks', dataIndex: 'highest_marks', key: 'highest_marks', align: 'center', render: (val) => val ? <Tag color="gold" style={{ fontWeight: 700 }}>{val}</Tag> : '-' },
    { title: 'Above 600', dataIndex: 'above_600', key: 'above_600', align: 'center', render: (val) => <Tag color="volcano" style={{ fontWeight: 700 }}>{val || 0}</Tag> },
    { title: 'Distinction', dataIndex: 'distinction', key: 'distinction', align: 'center', render: (val) => <Text style={{ color: '#059669', fontWeight: 600 }}>{val}</Text> },
    { title: 'First Class', dataIndex: 'first_class', key: 'first_class', align: 'center', render: (val) => <Text style={{ color: '#2563eb', fontWeight: 600 }}>{val}</Text> },
    { title: 'Second Class', dataIndex: 'second_class', key: 'second_class', align: 'center', render: (val) => <Text style={{ color: '#7c3aed', fontWeight: 600 }}>{val}</Text> },
    { title: 'PASS', dataIndex: 'pass', key: 'pass', align: 'center', render: (val) => <Text style={{ color: '#0891b2', fontWeight: 600 }}>{val}</Text> },
    { title: 'Total Pass', dataIndex: 'total_pass', key: 'total_pass', align: 'center', render: (val) => <Text style={{ color: '#16a34a', fontWeight: 700 }}>{val}</Text> },
    { title: 'Fail', dataIndex: 'fail', key: 'fail', align: 'center', render: (val) => <Text style={{ color: val > 0 ? '#dc2626' : '#9ca3af', fontWeight: 700 }}>{val}</Text> },
    {
      title: 'Pass %',
      dataIndex: 'pass_pct',
      key: 'pass_pct',
      align: 'center',
      render: (val) => {
        const color = val >= 95 ? 'green' : val >= 85 ? 'blue' : val >= 75 ? 'orange' : 'red';
        return <Tag color={color} style={{ fontWeight: 800, borderRadius: 10 }}>{val}%</Tag>;
      }
    }
  ];

  return (
    <Card
      title={
        <Space direction="vertical" size={1}>
          <Space>
            <ClusterOutlined style={{ color: '#3b82f6', fontSize: 18 }} />
            <Text strong style={{ color: 'var(--text-color, #1f2937)', fontSize: 16 }}>
              Candidates Performance Analytics
            </Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selectedYearName} • {selectedClusterName || 'All Clusters'}
          </Text>
        </Space>
      }
      extra={
        <Space size="small" wrap align="center">
          {/* Card-Level Academic Year Select */}
          <Select showSearch optionFilterProp="children"
            size="small"
            value={selectedYear}
            onChange={handleCardYearChange}
            style={{ width: 140 }}
            popupMatchSelectWidth={false}
          >
            <Option value="all">All Completed Academic Years</Option>
            {(completedAcademicYears || []).map((ay) => (
              <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                {ay.academic_year}
              </Option>
            ))}
          </Select>

          {/* Card-Level Cluster Select */}
          <Select showSearch optionFilterProp="children"
            size="small"
            value={selectedCluster}
            onChange={(val) => setSelectedCluster(val)}
            style={{ width: 150 }}
            popupMatchSelectWidth={false}
          >
            {!isStudio && <Option value="all">All Clusters</Option>}
            {availableClusters.map((cl) => (
              <Option key={cl.cluster_id} value={String(cl.cluster_id)}>
                {cl.cluster_name}
              </Option>
            ))}
          </Select>

          {/* View Mode */}
          <Segmented
            size="small"
            options={[
              { label: 'Both', value: 'both' },
              { label: 'Charts & Pie', value: 'charts' },
              { label: 'Institution Table', value: 'table' }
            ]}
            value={viewMode}
            onChange={(val) => setViewMode(val)}
          />

          {/* Download Buttons */}
          <Button
            size="small"
            icon={<FileExcelOutlined style={{ color: '#10b981' }} />}
            onClick={handleExportExcel}
            title="Export to Excel / CSV"
          >
            Excel
          </Button>
          <Button
            size="small"
            icon={<FilePdfOutlined style={{ color: '#ef4444' }} />}
            onClick={handleExportPDF}
            title=" Report"
          >
            PDF
          </Button>
        </Space>
      }
      style={{
        borderRadius: 16,
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <Spin spinning={cardLoading}>
        {/* Cluster Executive Summary Banner */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={12} md={6}>
            <Card bordered={false} size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(59, 130, 246, 0.1) 100%)', border: '1px solid rgba(59, 130, 246, 0.15)', height: '100%' }}>
              <Space size="middle">
                <TeamOutlined style={{ fontSize: 24, color: '#3b82f6' }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Total Students</Text>
                  <Text strong style={{ fontSize: 20, color: '#1e3a8a' }}>{summaryMetrics.totalStudents}</Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card bordered={false} size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(16, 185, 129, 0.1) 100%)', border: '1px solid rgba(16, 185, 129, 0.15)', height: '100%' }}>
              <Space size="middle">
                <CheckCircleOutlined style={{ fontSize: 24, color: '#10b981' }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Total Pass</Text>
                  <Text strong style={{ fontSize: 20, color: '#065f46' }}>{summaryMetrics.totalPass}</Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card bordered={false} size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(139, 92, 246, 0.1) 100%)', border: '1px solid rgba(139, 92, 246, 0.15)', height: '100%' }}>
              <Space size="middle">
                <LineChartOutlined style={{ fontSize: 24, color: '#8b5cf6' }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Cluster Pass %</Text>
                  <Text strong style={{ fontSize: 20, color: '#5b21b6' }}>{summaryMetrics.passPct}%</Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card bordered={false} size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(245, 158, 11, 0.1) 100%)', border: '1px solid rgba(245, 158, 11, 0.15)', height: '100%' }}>
              <Space size="middle">
                <TrophyOutlined style={{ fontSize: 24, color: '#f59e0b' }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Highest Marks</Text>
                  <Text strong style={{ fontSize: 20, color: '#92400e' }}>{summaryMetrics.highestMarks}</Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {processedInstitutions.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Empty description="No candidate performance data available for the selected Academic Year / Cluster." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {groupedInstitutions.map(({ standard, institutions }) => {
              const stdTotalStudents = institutions.reduce((acc, s) => acc + (s.total_students || 0), 0);
              const stdTotalPass = institutions.reduce((acc, s) => acc + (s.total_pass || 0), 0);
              const stdTotalFail = institutions.reduce((acc, s) => acc + (s.fail || 0), 0);
              const stdHighestMarks = Math.max(...institutions.map(s => s.highest_marks || 0), 0);
              const stdTotalDistinction = institutions.reduce((acc, s) => acc + (s.distinction || 0), 0);
              const stdTotalFirstClass = institutions.reduce((acc, s) => acc + (s.first_class || 0), 0);
              const stdTotalSecondClass = institutions.reduce((acc, s) => acc + (s.second_class || 0), 0);
              const stdTotalPassOnly = institutions.reduce((acc, s) => acc + (s.pass || 0), 0);
              const stdTotalAbove600 = institutions.reduce((acc, s) => acc + (s.above_600 || 0), 0);
              const stdPassPct = stdTotalStudents > 0 ? (stdTotalPass / stdTotalStudents * 100).toFixed(2) : '0.00';

              const rawDonutSlices = [
                { key: 'distinction', label: 'Distinction (85%+)', count: stdTotalDistinction, color: '#10b981' },
                { key: 'first_class', label: 'First Class (60%-84%)', count: stdTotalFirstClass, color: '#3b82f6' },
                { key: 'second_class', label: 'Second Class (50%-59%)', count: stdTotalSecondClass, color: '#8b5cf6' },
                { key: 'pass', label: 'Pass (35%-49%)', count: stdTotalPassOnly, color: '#06b6d4' },
                { key: 'fail', label: 'Fail (<35%)', count: stdTotalFail, color: '#f43f5e' }
              ];
              const stdTotalGradeCount = stdTotalStudents || 1;
              let currentOffset = 0;
              const stdDonutSlices = rawDonutSlices.map(item => {
                const count = item.count || 0;
                const pct = stdTotalGradeCount > 0 ? count / stdTotalGradeCount : 0;
                const strokeDashoffset = -currentOffset * 283;
                currentOffset += pct;
                return { ...item, pct, strokeDashoffset };
              });

              return (
                <div key={`${institutions[0]?.cluster_name || ''}||${standard}`} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Text strong style={{ fontSize: 16, display: 'block', color: '#1e3a8a', background: '#f0f9ff', padding: '8px 12px', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
                    {institutions[0]?.cluster_name ? `${institutions[0].cluster_name} - ` : ''}{standard}{institutions[0]?.academic_year_name ? ` - ${institutions[0].academic_year_name}` : ''}
                  </Text>

                  {(viewMode === 'both' || viewMode === 'charts') && (
                    <Row gutter={[20, 20]}>
                      {/* Grade Distribution Donut Pie Chart */}
                      <Col xs={24} lg={10}>
                        <Card
                          size="small"
                          title={<Text strong style={{ fontSize: 13 }}>🍩 {standard} Grade Share</Text>}
                          style={{ borderRadius: 12, background: 'rgba(0,0,0,0.01)', height: '100%' }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                            <div style={{ position: 'relative', width: 220, height: 220 }}>
                              <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                {stdDonutSlices.map((item) => {
                                  const strokeDasharray = `${item.pct * 283} 283`;
                                  return (
                                    <circle
                                      key={item.key}
                                      cx="50"
                                      cy="50"
                                      r="45"
                                      fill="transparent"
                                      stroke={item.color}
                                      strokeWidth="10"
                                      strokeDasharray={strokeDasharray}
                                      strokeDashoffset={item.strokeDashoffset}
                                      style={{ transition: 'stroke-dasharray 0.8s ease' }}
                                    />
                                  );
                                })}
                              </svg>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textAlign: 'center'
                                }}
                              >
                                <Text style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{stdPassPct}%</Text>
                                <Text type="secondary" style={{ fontSize: 14 }}>Pass Rate</Text>
                              </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16 }}>
                              {stdDonutSlices.map((item) => (
                                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                                  <Text style={{ fontSize: 11, fontWeight: 500 }}>{item.label}:</Text>
                                  <Text strong style={{ fontSize: 11 }}>{item.count}</Text>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Card>
                      </Col>

                      {/* Institution Performance Bar Chart */}
                      <Col xs={24} lg={14}>
                        <Card
                          size="small"
                          title={<Text strong style={{ fontSize: 13 }}>📊 Institution Pass Rate Comparison</Text>}
                          style={{ borderRadius: 12, background: 'rgba(0,0,0,0.01)', height: '100%' }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
                            {institutions.map((Institution, idx) => (
                              <div key={`${Institution.cluster_id}-${Institution.institution_id}-${Institution.code || idx}`}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                  <Text ellipsis style={{ maxWidth: '65%', fontSize: 12, fontWeight: 600 }}>
                                    {Institution.Institution_name}
                                  </Text>
                                  <Text style={{ fontSize: 11, fontWeight: 700, color: Institution.pass_pct >= 95 ? '#16a34a' : '#2563eb' }}>
                                    {Institution.pass_pct}% ({Institution.total_pass}/{Institution.total_students})
                                  </Text>
                                </div>
                                <Progress
                                  percent={Institution.pass_pct}
                                  showInfo={false}
                                  strokeColor={Institution.pass_pct >= 95 ? '#10b981' : Institution.pass_pct >= 85 ? '#3b82f6' : '#f59e0b'}
                                  size="small"
                                />
                              </div>
                            ))}
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {/* Detailed Institution Data Table matching Excel Sheet */}
                  {(viewMode === 'both' || viewMode === 'table') && (
                    <Table
                      dataSource={institutions}
                      columns={columns}
                      rowKey={(record) => `${record.cluster_id}-${record.institution_id}-${record.code}-${record.standard_id}`}
                      pagination={{ pageSize: 12 }}
                      size="small"
                      bordered
                      scroll={{ x: 1000 }}
                      summary={() => (
                        <Table.Summary fixed>
                          <Table.Summary.Row style={{ background: 'rgba(59, 130, 246, 0.08)', fontWeight: 800 }}>
                            <Table.Summary.Cell index={0} colSpan={2}>
                              <Text strong style={{ color: '#1e3a8a' }}>{institutions[0]?.cluster_name ? `${institutions[0].cluster_name} - ` : ''}{standard}{institutions[0]?.academic_year_name ? ` - ${institutions[0].academic_year_name}` : ''} SUMMARY</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="center">
                              <Text strong style={{ color: '#1e3a8a' }}>{stdTotalStudents}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                              <Tag color="gold" style={{ fontWeight: 800 }}>{stdHighestMarks}</Tag>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="center">
                              <Tag color="volcano" style={{ fontWeight: 800 }}>{stdTotalAbove600}</Tag>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="center">
                              <Text style={{ color: '#059669', fontWeight: 800 }}>{stdTotalDistinction}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} align="center">
                              <Text style={{ color: '#2563eb', fontWeight: 800 }}>{stdTotalFirstClass}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="center">
                              <Text style={{ color: '#7c3aed', fontWeight: 800 }}>{stdTotalSecondClass}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} align="center">
                              <Text style={{ color: '#0891b2', fontWeight: 800 }}>{stdTotalPassOnly}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={8} align="center">
                              <Text style={{ color: '#16a34a', fontWeight: 800 }}>{stdTotalPass}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={9} align="center">
                              <Text style={{ color: stdTotalFail > 0 ? '#dc2626' : '#9ca3af', fontWeight: 800 }}>{stdTotalFail}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={10} align="center">
                              <Tag color="green" style={{ fontSize: 13, fontWeight: 900, borderRadius: 10 }}>
                                {stdPassPct}% 
                              </Tag>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Spin>
    </Card>
  );
};
